import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import ListingCard from "../components/ListingCard";
import CityAutocomplete from "../components/CityAutocomplete";
import { CATEGORIES, TYPE_LABELS } from "../lib/categories";
import { Search, X, SlidersHorizontal } from "lucide-react";

export default function Browse() {
  const { lang, t } = useI18n();
  const [sp, setSp] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState(sp.get("q") || "");
  const [category, setCategory] = useState(sp.get("category") || "");
  const [city, setCity] = useState(sp.get("city") || "");
  const [listingType, setListingType] = useState(sp.get("listing_type") || "");
  const [minPrice, setMinPrice] = useState(sp.get("min_price") || "");
  const [maxPrice, setMaxPrice] = useState(sp.get("max_price") || "");
  const [nearCity, setNearCity] = useState(sp.get("near_city") || "");
  const [radiusKm, setRadiusKm] = useState(sp.get("radius_km") || "");

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (category) params.category = category;
    if (city) params.city = city;
    if (listingType) params.listing_type = listingType;
    if (minPrice) params.min_price = minPrice;
    if (maxPrice) params.max_price = maxPrice;
    if (nearCity && radiusKm) {
      params.near_city = nearCity;
      params.radius_km = radiusKm;
    }
    try {
      const r = await api.get("/listings", { params });
      setListings(r.data);
    } catch (_e) { /* noop */ }
    setLoading(false);
  }, [q, category, city, listingType, minPrice, maxPrice, nearCity, radiusKm]);

  useEffect(() => { load(); }, [load]);

  // sync URL params
  useEffect(() => {
    const obj = {};
    if (q) obj.q = q;
    if (category) obj.category = category;
    if (city) obj.city = city;
    if (listingType) obj.listing_type = listingType;
    if (minPrice) obj.min_price = minPrice;
    if (maxPrice) obj.max_price = maxPrice;
    if (nearCity) obj.near_city = nearCity;
    if (radiusKm) obj.radius_km = radiusKm;
    setSp(obj, { replace: true });
  }, [q, category, city, listingType, minPrice, maxPrice, nearCity, radiusKm, setSp]);

  const reset = () => {
    setQ(""); setCategory(""); setCity(""); setListingType(""); setMinPrice(""); setMaxPrice(""); setNearCity(""); setRadiusKm("");
  };

  const hasFilters = q || category || city || listingType || minPrice || maxPrice || nearCity;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Search bar */}
      <div className="bg-white border border-[#D1CFC7] rounded-md p-5 mb-6 hard-shadow">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex items-center bg-white border border-[#D1CFC7] rounded-md flex-1 focus-within:ring-2 focus-within:ring-[#C85A32]">
            <Search className="w-4 h-4 ml-3 text-[#7A7A73]"/>
            <input
              data-testid="browse-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search_placeholder")}
              className="w-full px-2 py-2.5 bg-transparent outline-none text-sm"
            />
          </div>
          <CityAutocomplete value={city} onChange={setCity} testId="browse-city" />
          <select
            data-testid="browse-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-white border border-[#D1CFC7] rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#C85A32] outline-none"
          >
            <option value="">{t("all_categories")}</option>
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c[lang]}</option>)}
          </select>
          <select
            data-testid="browse-type"
            value={listingType}
            onChange={(e) => setListingType(e.target.value)}
            className="bg-white border border-[#D1CFC7] rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#C85A32] outline-none"
          >
            <option value="">{t("all_types")}</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v[lang]}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-3 mt-3 items-center">
          <div className="flex items-center gap-2 text-sm">
            <SlidersHorizontal className="w-4 h-4 text-[#7A7A73]"/>
            <input
              data-testid="browse-min-price"
              type="number"
              min="0"
              placeholder={`${t("price")} ↓`}
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              className="bg-white border border-[#D1CFC7] rounded-md px-2 py-1.5 w-24 outline-none focus:ring-2 focus:ring-[#C85A32]"
            />
            <span>—</span>
            <input
              data-testid="browse-max-price"
              type="number"
              min="0"
              placeholder={`${t("price")} ↑`}
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              className="bg-white border border-[#D1CFC7] rounded-md px-2 py-1.5 w-24 outline-none focus:ring-2 focus:ring-[#C85A32]"
            />
            <span className="text-[#7A7A73]">€</span>
          </div>

          {/* Distance filter */}
          <div className="flex items-center gap-2 text-sm border-l border-[#D1CFC7] pl-3">
            <span className="text-[#7A7A73] text-xs font-semibold uppercase tracking-widest">Šalia</span>
            <div className="w-44">
              <CityAutocomplete value={nearCity} onChange={setNearCity} testId="browse-near-city" placeholder="Mano miestas" />
            </div>
            <select
              data-testid="browse-radius"
              value={radiusKm}
              onChange={(e) => setRadiusKm(e.target.value)}
              disabled={!nearCity}
              className="bg-white border border-[#D1CFC7] rounded-md px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#C85A32] disabled:bg-[#F0EFEB]"
            >
              <option value="">– km –</option>
              <option value="2">2 km</option>
              <option value="5">5 km</option>
              <option value="10">10 km</option>
              <option value="25">25 km</option>
              <option value="50">50 km</option>
              <option value="100">100 km</option>
            </select>
          </div>

          {hasFilters && (
            <button
              data-testid="browse-reset"
              onClick={reset}
              className="ml-auto inline-flex items-center gap-1 text-sm text-[#C85A32] font-semibold hover:underline"
            >
              <X className="w-3 h-3"/> Reset
            </button>
          )}
        </div>
      </div>

      <div className="mb-4 text-sm text-[#7A7A73]" data-testid="browse-count">
        {loading ? "..." : `${listings.length} ${t("nav_browse").toLowerCase()}`}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="card-surface aspect-[4/5] bg-[#F0EFEB]"/>
          ))}
        </div>
      ) : listings.length === 0 ? (
        <div className="text-center py-20" data-testid="browse-empty-state">
          <p className="text-2xl font-heading font-bold text-[#1A1A18]">
            {city || nearCity ? t("no_city_listings") : t("no_results")}
          </p>
          {(city || nearCity) ? (
            <button
              data-testid="search-nationwide-button"
              onClick={() => { setCity(""); setNearCity(""); setRadiusKm(""); }}
              className="mt-5 btn-primary inline-flex items-center gap-2"
            >
              <Search className="w-4 h-4" /> {t("search_nationwide")}
            </button>
          ) : (
            <p className="text-[#7A7A73] mt-2">{t("try_other")}</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {listings.map((l) => (
            <ListingCard
              key={l.id}
              listing={l}
              onDeleted={(id) => setListings((cur) => cur.filter((x) => x.id !== id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
